import { TREND_PERIODS } from '@/lib/metric-registry/trend'

import { z } from 'zod'

/**
 * The catalog of user preference keys — one entry per thing a user can set.
 *
 * WHY THIS LIVES IN THE CLIENT, not wv-db-schemas alongside permissions.config
 * and data-tables.config: those two are in the package because schemas-side
 * scripts consume them (check-permission-orphans validates DB grant rows
 * against the permission catalog; the data-tables registry seeds derived grant
 * ids). Nothing analogous exists for preferences — the storage is a plain
 * `(user_id, preference_key, value)` KV, writes are validated at runtime by
 * this catalog, and only the client reads it. Putting it in the package would
 * mean a cross-repo release for every new key, and the localStorage migration
 * adds a dozen.
 *
 * ADDING A KEY: add an entry here. That's the whole job — the API's allowlist,
 * its per-key validation, and `usePreference()`'s typing all derive from it.
 */

/**
 * One preference. `value` is TEXT in the DB, so every entry says how it
 * crosses that boundary as well as how to validate it.
 */
export type PreferenceEntry<T> = {
  /** Validates the DECODED value. A write that fails is rejected with a 400. */
  schema: z.ZodType<T>
  /** Used when the user has no row for this key. */
  defaultValue: T
  /** Why this exists, for whoever reads the catalog next. */
  description: string
  encode: (value: T) => string | null
  decode: (raw: string | null) => unknown
}

// NOTE: referential checks (is this a real app key, is the caller a member of
// this org) deliberately do NOT live here. They need the DB and the app
// registry, and this file is imported by client components — so it has to stay
// free of `server-only` deps. See SERVER_CHECKS in api/preferences/route.ts.

function bool(defaultValue: boolean, description: string) {
  return {
    schema: z.boolean(),
    defaultValue,
    description,
    encode: (v: boolean) => (v ? 'true' : 'false'),
    // Tolerates the '1'/'0' spelling several localStorage keys used before the
    // migration, so a backfilled value doesn't silently read as false.
    decode: (raw: string | null) =>
      raw === null ? null : raw === 'true' || raw === '1'
  } satisfies PreferenceEntry<boolean>
}

function num(
  schema: z.ZodType<number>,
  defaultValue: number,
  description: string
) {
  return {
    schema,
    defaultValue,
    description,
    encode: (v: number) => String(v),
    decode: (raw: string | null) => {
      if (raw === null) return null
      const n = Number(raw)
      return Number.isFinite(n) ? n : null
    }
  } satisfies PreferenceEntry<number>
}

/** A pinned id where null means "not pinned — resolve automatically". */
function pinnedId(description: string) {
  return {
    schema: z.string().min(1).nullable(),
    defaultValue: null,
    description,
    encode: (v: string | null) => v,
    decode: (raw: string | null) => raw
  } satisfies PreferenceEntry<string | null>
}

/**
 * A structured value, stored as JSON text.
 *
 * The audit called stringified JSON in a text column a foot-gun and wanted a
 * `value_json jsonb` column. That concern was about scattered hand-rolled
 * JSON.parse; here the codec is the ONLY place that parses, and the zod schema
 * validates the decoded object, so a malformed row reads as the default instead
 * of propagating. Nothing queries inside a preference value either — they are
 * always read whole by key — so jsonb buys no operators we would use. Not worth
 * a cross-repo migration.
 */
function json<T>(schema: z.ZodType<T>, defaultValue: T, description: string) {
  return {
    schema,
    defaultValue,
    description,
    encode: (v: T) => JSON.stringify(v),
    decode: (raw: string | null) => {
      if (raw === null) return null
      try {
        return JSON.parse(raw)
      } catch {
        // Unparseable row — let the schema reject it so the default applies.
        return null
      }
    }
  } satisfies PreferenceEntry<T>
}

/**
 * A JSON object of independent entries keyed by id, decoded entry by entry.
 * With `json` one malformed entry fails the record schema and the whole value
 * reads as `{}`; here it is dropped and every other entry survives.
 */
function jsonRecord<T>(entry: z.ZodType<T>, description: string) {
  const base = json(
    z.record(z.string(), entry),
    {} as Record<string, T>,
    description
  )
  return {
    ...base,
    decode: (raw: string | null) => {
      const parsed: unknown = base.decode(raw)
      if (typeof parsed !== 'object' || parsed === null) return null
      if (Array.isArray(parsed)) return null
      return Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => entry.safeParse(v).success)
      )
    }
  } satisfies PreferenceEntry<Record<string, T>>
}

/**
 * How many panel opens still show the move/resize hint. Lives here because the
 * schema bound and the shell's cut-off have to be the same number.
 */
export const VUE_PANEL_HINT_LIMIT = 3

/** Most tiles the Metrics widget holds (a large widget shows six). */
export const MAX_METRIC_TILES = 6

export const PREFERENCES = {
  // ─── Landing ──────────────────────────────────────────────────────────────
  // `me` is deliberately not a valid value — My Atlas is a personal area, not
  // a work surface. That check, and the org-membership one, are enforced by
  // SERVER_CHECKS in the route.
  default_application: pinnedId(
    'App to open after signing in; null resolves by access priority.'
  ),
  default_organization: pinnedId(
    'Org to open by default; null shows the picker.'
  ),

  // ─── App switcher ─────────────────────────────────────────────────────────
  // The apps a user has starred, surfaced as the switcher's Favorites section.
  favorite_applications: json(
    z.array(z.string()),
    [],
    'App keys the user has starred, shown as the switcher Favorites section.'
  ),

  // ─── Chrome / panel state ─────────────────────────────────────────────────
  // The main sidebar. Was a cookie, so SSR could read it before paint — but the
  // authed layout already awaits the preference payload, so it can read the real
  // value directly and the cookie is redundant.
  'ui.sidebar_open': bool(true, 'Main navigation sidebar expanded.'),

  /**
   * Light / dark / system.
   *
   * next-themes owns the theme AT PAINT TIME — it reads localStorage in an inline
   * <script> before first paint, which is how it avoids a flash — so this
   * preference is not the paint-time source and cannot be. It is the DURABLE
   * RECORD, written when the user picks a theme and read only to seed a device
   * that has never chosen one (see ThemePreferenceSeeder).
   *
   * The shape matters: an earlier attempt mirrored next-themes' state with a pair
   * of effects, which loops (Maximum update depth) and took down every authed
   * page. Write on INTENT, read ONCE. Never watch `theme` and write.
   */
  'ui.theme': {
    schema: z.enum(['light', 'dark', 'system']),
    defaultValue: 'system' as 'light' | 'dark' | 'system',
    description: 'Colour theme. next-themes owns paint; this is the record.',
    encode: (v: 'light' | 'dark' | 'system') => v,
    decode: (raw: string | null) => raw
  } satisfies PreferenceEntry<'light' | 'dark' | 'system'>,
  /**
   * Masks dollar amounts on screen (MRR, ARR, spend) for screen sharing or a
   * colleague at the desk. Follows the user, like the theme, and is chosen in
   * the same appearance menu. Display only: it hides nothing from exports or
   * from the API, and permissions still decide who receives the money at all.
   */
  'ui.hide_amounts': bool(false, 'Mask dollar amounts on screen.'),
  // Migrated off localStorage. Each was a per-device boolean; they follow the
  // user now. localStorage stays the first-paint cache — these control layout,
  // so waiting on a round-trip would flash.
  'ui.timeline_sidebar_open': bool(
    false,
    'Customer timeline sidebar expanded.'
  ),
  'ui.portal_cover_collapsed': bool(
    false,
    'Portal property cover header collapsed.'
  ),
  'ui.smartbox_header_collapsed': bool(false, 'Smartbox header collapsed.'),
  'ui.advanced_search_rail_collapsed': bool(
    false,
    'Advanced-search rail collapsed.'
  ),
  'ui.table_filter_rail_collapsed': bool(
    false,
    'Data-table Views & filters rail collapsed.'
  ),

  // ─── Technician Portal ────────────────────────────────────────────────────
  // Follows the USER, not the device, on purpose: a technician who did the
  // tour on their laptop should not be handed it again on the shop tablet.
  'ui.technician_tour_seen': bool(
    false,
    'Technician guided tour finished or dismissed; suppresses auto-start.'
  ),

  // ─── Guided tours ─────────────────────────────────────────────────────────
  // One key for every tour, keyed by tour id, so a new tour needs no catalog
  // edit. `v` is the tour version the record was written for: a tour whose
  // definition is newer is offered again. Decoded per entry, so one bad record
  // can't erase the others on the next write. Merge rules are in the tours
  // engine (features/tours/engine/persistence.ts).
  'ui.tours': jsonRecord(
    z.object({
      status: z.enum(['completed', 'dismissed']),
      at: z.string(),
      v: z.number().int().min(1)
    }),
    'Guided tours completed or dismissed, per tour id, with the tour version.'
  ),

  // ─── Hub servers ──────────────────────────────────────────────────────────
  // Hubs report their clock in 24-hour (`date '+%Y-%m-%d %H:%M:%S'`), which is
  // what an operator comparing it to a log wants; US support staff generally
  // read 12-hour. Per-user rather than per-device: the same agent should get
  // the same reading on the desk and on the laptop.
  'ui.hub_clock_12h': bool(
    false,
    'Show hub server clocks in 12-hour time; hubs report 24-hour.'
  ),

  // ─── Room grid ────────────────────────────────────────────────────────────
  // Structured, one entry per app because the two grids expose different data
  // views (a portal user picking up an internal-only view was already a bug the
  // component guarded against). Loose objects rather than an exhaustive schema:
  // the grid owns these shapes and adds to them, and a preference should not
  // need a catalog edit every time it grows a toggle.
  'room_grid.internal': json(
    z.record(z.string(), z.unknown()),
    {},
    'Room-grid view settings, data view and navigation state (internal).'
  ),
  'room_grid.portal': json(
    z.record(z.string(), z.unknown()),
    {},
    'Room-grid view settings, data view and navigation state (portal).'
  ),

  // ─── Vue ──────────────────────────────────────────────────────────────────
  // Counted rather than a flag: two gestures take more than one exposure to
  // learn
  'vue.panel_hint_shown': num(
    z.number().int().min(0).max(VUE_PANEL_HINT_LIMIT),
    0,
    'Times the Vue panel move/resize hint has been shown.'
  ),
  'vue.dev_mode': bool(
    false,
    'Vue developer view. System admins only; the UI gates visibility.'
  ),
  // Null until the viewer moves or resizes the card: position then means
  // "anchor bottom-right", size means "derive from the viewport".
  //
  // The real floor and ceiling are PANEL_MIN_*/PANEL_MAX_* in use-panel-frame, and they cannot live here:
  // the floor loses to what actually fits, so a short viewport legitimately resolves below it.
  'vue.panel_frame': json(
    z.object({
      x: z.number().nullable(),
      y: z.number().nullable(),
      w: z.number().int().min(120).max(4000).nullable(),
      h: z.number().int().min(120).max(4000).nullable()
    }),
    { x: null, y: null, w: null, h: null },
    'Vue panel position and size in px; null derives from the viewport.'
  ),
  'vue.sidebar_open': bool(true, 'Vue workspace sidebar expanded.'),
  'vue.pinned_open': bool(true, 'Vue sidebar Pinned section expanded.'),
  'vue.chats_open': bool(true, 'Vue sidebar Chats section expanded.'),
  'vue.console_open': bool(true, 'Vue sidebar Console section expanded.'),

  // ─── Mobile home-screen widget ────────────────────────────────────────────
  // Follows the USER across devices. Written through the Metrics selection
  // endpoint, which drops tiles the caller may not see; a row written any
  // other way is filtered again at read time, so this is never an access
  // decision. Empty `ids` means "use my role's default".
  'mobile.metric_tiles': json(
    z.object({
      ids: z.array(z.string().min(1)).max(MAX_METRIC_TILES),
      scope: z.enum(['me', 'team']),
      // Only periods the person chose; a tile without one uses its metric's
      // suggested period. Optional so rows saved before periods still read.
      periods: z.record(z.string().min(1), z.enum(TREND_PERIODS)).optional()
    }),
    { ids: [] as string[], scope: 'me' as 'me' | 'team' },
    'Metrics widget tile ids in display order, the Sales Me/Team scope, and per-tile trend periods.'
  )
}

export type PreferenceKey = keyof typeof PREFERENCES

/** The decoded type of a preference's value. */
export type PreferenceValue<K extends PreferenceKey> =
  (typeof PREFERENCES)[K] extends PreferenceEntry<infer T> ? T : never

/**
 * Keys whose value is a boolean. Components that persist a simple on/off (rail
 * collapse, panel open) take one of these, so their setter is `(v: boolean)`
 * rather than the union of every preference type — which TypeScript would
 * otherwise narrow to `never`.
 */
export type BooleanPreferenceKey = {
  [K in PreferenceKey]: PreferenceValue<K> extends boolean ? K : never
}[PreferenceKey]

export const PREFERENCE_KEYS = Object.keys(PREFERENCES) as PreferenceKey[]

export function isPreferenceKey(key: string): key is PreferenceKey {
  return Object.hasOwn(PREFERENCES, key)
}

/** Untyped accessor for the generic paths (route validation, bulk decode). */
export function preferenceEntry(key: PreferenceKey): PreferenceEntry<unknown> {
  return PREFERENCES[key] as unknown as PreferenceEntry<unknown>
}

/**
 * Decode a stored text value, falling back to the catalog default.
 *
 * `undefined` means "no row" (use the default). `null` is a stored NULL, which
 * is meaningful for the pinned-id keys and distinct from absent.
 */
export function decodePreference<K extends PreferenceKey>(
  key: K,
  raw: string | null | undefined
): PreferenceValue<K> {
  const def = preferenceEntry(key)
  if (raw === undefined) return def.defaultValue as PreferenceValue<K>
  const parsed = def.schema.safeParse(def.decode(raw))
  // A stored value that no longer validates (hand-edited row, or a schema we
  // later tightened) reads as the default rather than propagating garbage.
  return (parsed.success ? parsed.data : def.defaultValue) as PreferenceValue<K>
}

/** Encode a value for storage, or return why it was rejected. */
export function encodePreference(
  key: PreferenceKey,
  value: unknown
):
  | { ok: true; encoded: string | null; parsed: unknown }
  | { ok: false; error: string } {
  const def = preferenceEntry(key)
  const parsed = def.schema.safeParse(value)
  if (!parsed.success) {
    return { ok: false, error: parsed.error.issues[0]?.message ?? 'invalid' }
  }
  return { ok: true, encoded: def.encode(parsed.data), parsed: parsed.data }
}
